"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.disambiguate = void 0;
const RUNG_SEP = '\n#';
const groupBySessionFp = (records) => {
    const groups = new Map();
    for (const record of records) {
        let byFp = groups.get(record.sessionId);
        if (!byFp) {
            byFp = new Map();
            groups.set(record.sessionId, byFp);
        }
        let members = byFp.get(record.signatureText);
        if (!members) {
            members = [];
            byFp.set(record.signatureText, members);
        }
        members.push(record);
    }
    return groups;
};
/**
 * The hard-guarantee collision predicate: >=2 distinct `axeViewId`s. A group
 * that is only the same view repeated once per failing rule is not a collision.
 */
const distinctIdsCollide = (members) => new Set(members.map(m => m.axeViewId)).size >= 2;
/**
 * A signature is ambiguous when the screen carries it more than once, whether
 * or not every carrier failed a rule. Testing the failing members instead would
 * make the suffix appear and disappear as neighbours start and stop failing, so
 * one element would not keep a single identity across page states of the same
 * screen. Falls back to the members when the caller has no peer index.
 */
const isAmbiguous = (members, peers) => (peers ? peers.length >= 2 : distinctIdsCollide(members));
/**
 * Fallback rank, used when the peer lookup does not cover every member. Dense
 * over the members themselves rather than the raw `viewOrder`, which counts
 * every view in the tree: a node added anywhere earlier in DFS order shifts it.
 */
const rankByViewOrder = (members) => {
    const orderById = new Map();
    for (const member of members) {
        if (!orderById.has(member.axeViewId)) {
            orderById.set(member.axeViewId, member.viewOrder);
        }
    }
    const ranked = [...orderById.entries()].sort(([, a], [, b]) => a - b);
    return new Map(ranked.map(([id], rank) => [id, rank]));
};
/**
 * Ranks every view on the screen carrying the group's signature, not only the
 * ones that failed. Ranking the failing subset alone would make the suffix a
 * function of which elements happened to fail, so a row would inherit its
 * neighbour's identity the moment that neighbour stopped failing — merging two
 * distinct rows across page states that resolve to the same screen.
 */
const rankByPeerOrder = (members, peers) => {
    const ranked = new Map((peers ?? []).map((id, rank) => [id, rank]));
    if (members.every(m => ranked.has(m.axeViewId))) {
        return ranked;
    }
    return rankByViewOrder(members);
};
/**
 * Final fingerprint per issue, keyed by `iid`. Records that never collide, and
 * records whose `signatureText` is empty, are returned unchanged.
 *
 * Guarantee: after the pass, two records that shared a `sessionId` and a
 * non-empty base `signatureText` with distinct `axeViewId`s no longer share
 * a final fingerprint, and a record's fingerprint does not depend on which
 * other views happened to fail. It does not extend across base signatures: a
 * suffixed fingerprint can in principle equal another record's
 * already-suffixed-looking base signature.
 *
 * Only `'ladder'` is implemented; any other value throws, so a mistyped config
 * fails loudly instead of quietly leaving every collision un-suffixed.
 */
const disambiguate = (issues, strategy, peersOf) => {
    if (strategy !== 'ladder') {
        throw new Error(`Unknown disambiguation strategy: ${JSON.stringify(strategy)}`);
    }
    const out = {};
    const fingerprintable = [];
    for (const issue of issues) {
        out[issue.iid] = issue.signatureText;
        if (issue.signatureText !== '') {
            fingerprintable.push(issue);
        }
    }
    for (const sessionGroups of groupBySessionFp(fingerprintable).values()) {
        for (const members of sessionGroups.values()) {
            const peers = peersOf(members[0].signatureText);
            if (!isAmbiguous(members, peers)) {
                continue;
            }
            const rank = rankByPeerOrder(members, peers);
            for (const member of members) {
                out[member.iid] =
                    `${member.signatureText}${RUNG_SEP}idx=${rank.get(member.axeViewId)}`;
            }
        }
    }
    return out;
};
exports.disambiguate = disambiguate;
//# sourceMappingURL=disambiguation.js.map